import { Link } from "react-router-dom";
import { SEO, serviceSchema, breadcrumbSchema } from "@/components/seo";
import { CTASection } from "@/components/common/CTASection";
import { FiArrowRight, FiCheck, FiFileText, FiDollarSign, FiClock, FiTrendingUp } from "@/lib/icons";
import { broker } from "@/config/broker.config";

const benefits = [
  {
    icon: <FiDollarSign className="size-6" />,
    title: "Immediate cash flow",
    description: "Access up to 80-90% of your invoice value within 24-48 hours, instead of waiting 30-90 days for payment.",
  },
  {
    icon: <FiFileText className="size-6" />,
    title: "No property security",
    description: "Your invoices are the security. No need to put up property or other assets as collateral.",
  },
  {
    icon: <FiTrendingUp className="size-6" />,
    title: "Grows with you",
    description: "Your facility grows as your sales grow. The more you invoice, the more you can access.",
  },
  {
    icon: <FiClock className="size-6" />,
    title: "Flexible facility",
    description: "Use it when you need it. Only pay for what you use, not a fixed loan amount.",
  },
];

const howItWorks = [
  {
    step: 1,
    title: "Issue your invoice",
    description: "You deliver goods or services to your customer and issue an invoice as normal.",
  },
  {
    step: 2,
    title: "Submit to lender",
    description: "Upload the invoice to the finance provider's platform (usually simple and automated).",
  },
  {
    step: 3,
    title: "Receive advance",
    description: "Receive 80-90% of the invoice value within 24-48 hours, credited to your account.",
  },
  {
    step: 4,
    title: "Customer pays",
    description: "Your customer pays the invoice. The lender releases the remaining balance minus fees.",
  },
];

const types = [
  {
    title: "Invoice Factoring",
    description: "The lender takes over collection of your invoices. Your customers pay the lender directly. Can include credit protection.",
    disclosure: "Disclosed to customers",
    collection: "Lender collects",
  },
  {
    title: "Invoice Discounting",
    description: "You maintain the customer relationship and collect payments yourself. The facility operates behind the scenes.",
    disclosure: "Confidential (usually)",
    collection: "You collect",
  },
  {
    title: "Selective Invoice Finance",
    description: "Finance specific invoices as needed, rather than your whole ledger. Good for occasional cash flow gaps.",
    disclosure: "Varies",
    collection: "Varies",
  },
];

const suitedFor = [
  "Businesses with B2B customers (invoicing other businesses)",
  "Growing businesses where cash flow lags behind sales",
  "Seasonal businesses with fluctuating cash needs",
  "Businesses with long payment terms (30-90 days)",
  "Companies wanting to take on larger orders",
  "Businesses without property to use as security",
];

export function InvoiceFinancePage() {
  return (
    <>
      <SEO
        title="Invoice Finance Brisbane, Ipswich & Springfield"
        description="Turn unpaid invoices into immediate cash flow. Invoice factoring and discounting for B2B businesses in Brisbane, Ipswich, Springfield & Logan."
        url="/business-loans/invoice-finance"
        keywords={[
          "invoice finance Brisbane",
          "invoice factoring Brisbane",
          "invoice discounting Ipswich",
          "debtor finance",
          "cash flow finance",
        ]}
        jsonLd={[
          serviceSchema({
            name: "Invoice Finance",
            description:
              "Invoice factoring and discounting to unlock cash trapped in unpaid B2B receivables. Compared from specialist lenders.",
            url: "/business-loans/invoice-finance",
            serviceType: "Invoice Finance",
          }),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Business Loans", url: "/business-loans" },
            { name: "Invoice Finance", url: "/business-loans/invoice-finance" },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Link
                to="/business-loans"
                className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6 transition-colors"
              >
                <FiArrowRight className="size-4 mr-2 rotate-180" />
                Business Loans
              </Link>
              <p className="section-label mb-4">Invoice Finance</p>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
                Turn invoices into immediate cash
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                Why wait 30, 60, or 90 days for customers to pay? Invoice finance
                unlocks the cash tied up in your receivables, improving cash flow
                without traditional debt.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-medium hover:bg-primary/90 transition-colors"
                >
                  Discuss Your Options
                  <FiArrowRight className="size-4" />
                </Link>
                <Link
                  to={`tel:${broker.phone.replace(/\s/g, "")}`}
                  className="inline-flex items-center gap-2 border border-border px-6 py-3 rounded-full font-medium hover:bg-white transition-colors"
                >
                  Call {broker.phone}
                </Link>
              </div>
            </div>
            <div className="bg-primary text-primary-foreground rounded-3xl p-8">
              <p className="text-primary-foreground/90 text-sm mb-2">Example</p>
              <p className="font-serif text-4xl mb-2">$100,000</p>
              <p className="text-primary-foreground/90 text-sm mb-6">
                invoice due in 60 days
              </p>
              <div className="border-t border-primary-foreground/20 pt-6 space-y-3">
                <div className="flex justify-between">
                  <span className="text-primary-foreground/90">Advance (85%)</span>
                  <span className="font-semibold">$85,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-primary-foreground/90">Available in</span>
                  <span className="font-semibold">24-48 hours</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-primary-foreground/90">Balance on payment</span>
                  <span className="font-semibold">$15,000 - fees</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Benefits</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              Why use invoice finance?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit) => (
              <div
                key={benefit.title}
                className="bg-white rounded-2xl p-6 border border-border/50 text-center"
              >
                <div className="p-3 bg-accent/20 rounded-xl inline-flex mb-4 text-primary">
                  {benefit.icon}
                </div>
                <h3 className="font-semibold mb-2">{benefit.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Process</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              How invoice finance works
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((item) => (
              <div key={item.step} className="bg-white rounded-2xl p-6 border border-border/50">
                <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-serif text-lg mb-4">
                  {item.step}
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Types */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Options</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              Types of invoice finance
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Different structures suit different needs. We'll help you choose the
              right option for your business.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {types.map((type) => (
              <div
                key={type.title}
                className="bg-white rounded-2xl p-6 border border-border/50"
              >
                <h3 className="font-semibold text-lg mb-3">{type.title}</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  {type.description}
                </p>
                <div className="space-y-2 pt-4 border-t border-border">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Disclosure:</span>
                    <span className="font-medium">{type.disclosure}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Collection:</span>
                    <span className="font-medium">{type.collection}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Suited for */}
      <section className="py-16 md:py-20 bg-primary text-primary-foreground">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="section-label mb-4">
                Is It Right For You?
              </p>
              <h2 className="font-serif text-3xl md:text-4xl mb-6">
                Invoice finance is suited for
              </h2>
              <p className="text-primary-foreground/80">
                Invoice finance works best for businesses that invoice other
                businesses (B2B) and have reliable customers. It's not typically
                suited for retail or consumer businesses.
              </p>
            </div>
            <div className="bg-white/10 rounded-2xl p-6">
              <ul className="space-y-3">
                {suitedFor.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <FiCheck className="size-5 text-primary-foreground flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        label="Improve your cash flow"
        title="Let's discuss if invoice finance is right for you"
        description="Book a free consultation and we'll assess your options."
        primaryAction={{ label: "Get Started", href: "/contact" }}
        secondaryAction={{
          label: "Call Us",
          href: `tel:${broker.phone.replace(/\s/g, "")}`,
        }}
      />
    </>
  );
}
